<script setup lang="ts">
import type { StationsSearchResponse } from '~/server/api/stations/index.types'
import { computed, ref, watch } from 'vue'
import type { StationListItem } from '~/types/stations'
import { useInfiniteScroll } from '@vueuse/core'

const {
  title = '',
  query = '',
  genre,
  country,
  language,
  limit = 10,
} = defineProps<{
  title?: string
  query?: string
  genre?: string
  country?: string
  language?: string
  limit?: number
}>()

const stationList = ref<StationListItem[]>([])
const page = ref(1)
const hasMore = ref(true)

const {
  data: stationsData,
  status,
  refresh,
} = await useAsyncData<StationsSearchResponse>('stations', () =>
  $fetch('/api/stations/', {
    headers: useRequestHeaders(['x-forwarded-for', 'user-agent']),
    params: {
      query,
      limit,
      genre,
      country,
      language,
      page: page.value,
    },
  })
)

if (stationsData.value?.data) {
  stationList.value = stationsData.value.data
}

const isLoading = computed(() => status.value === 'pending')

watch(
  () => query,
  async () => {
    page.value = 1
    stationList.value = []
    await refresh()
  }
)

watch(stationsData, (response) => {
  if (response?.data?.length) {
    stationList.value = page.value === 1 ? response.data : [...stationList.value, ...response.data]
    hasMore.value = response.data.length === limit
  } else {
    hasMore.value = false
  }
})

useInfiniteScroll(
  document,
  async () => {
    if (isLoading.value || !hasMore.value) return
    page.value += 1
    await refresh()
  },
  { distance: 60, interval: 500 }
)
</script>

<template>
  <section>
    <h2 v-if="title" class="text-lg mb-4">{{ title }}</h2>

    <div v-else-if="!stationList.length && !isLoading" class="text-center p-16 text-lg">
      No results were found that match your criteria.
    </div>

    <StationListGrid v-if="stationList.length" :items="stationList" />

    <Icon v-if="isLoading" size="48" name="mingcute:loading-3-fill" class="animate-spin mx-auto mt-4" />
  </section>
</template>

<style scoped></style>
